/**
 * Removes dangerous nodes and attributes from a DocumentFragment or Element
 * before injecting it into the DOM.
 *
 * Strips:
 * - <script> elements
 * - on* event handler attributes (onclick, onload, onerror, …)
 * - href/src/action attributes starting with "javascript:"
 *
 * Iframes, embeds and other tags needed for booking tools are preserved.
 *
 * @param {DocumentFragment | Element} node
 */
function sanitizeFragment(node) {
  const scripts = node.querySelectorAll('script');
  for (const script of scripts) {
    script.remove();
  }

  const allElements = node.querySelectorAll('*');
  for (const el of allElements) {
    for (const attr of [...el.attributes]) {
      if (/^on/i.test(attr.name)) {
        el.removeAttribute(attr.name);
        continue;
      }
      if (['href', 'src', 'action'].includes(attr.name.toLowerCase())) {
        if (/^\s*javascript:/i.test(attr.value)) {
          el.removeAttribute(attr.name);
        }
      }
    }
  }
}

/**
 * BookingLayer component — extends LayerComponent to open a booking form dialog
 * when any link with href="#booking-{handle}" is clicked anywhere on the page.
 *
 * Content comes from <template data-booking-handle="..."> elements pre-rendered
 * in the Liquid snippet, so no async fetch is needed.
 *
 * @extends {LayerComponent}
 */
customElements.whenDefined('layer-component').then(() => {
  const LayerComponentClass = customElements.get('layer-component');

  class BookingLayer extends LayerComponentClass {
    connectedCallback() {
      super.connectedCallback();
      this.#init();
    }

    #init() {
      document.addEventListener('click', (event) => this.#handleDocumentClick(event));
      this.dialogElement.addEventListener('close', () => this.#clearContent());
    }

    /**
     * @param {MouseEvent} event
     */
    #handleDocumentClick(event) {
      const link = /** @type {Element} */ (event.target).closest('a[href^="#booking-"]');
      if (!link) return;

      event.preventDefault();
      const handle = link.getAttribute('href').slice('#booking-'.length);
      this.#openForHandle(handle);
    }

    /**
     * @param {string} handle
     */
    #openForHandle(handle) {
      const template = /** @type {HTMLTemplateElement | null} */ (
        this.querySelector(`template[data-booking-handle="${CSS.escape(handle)}"]`)
      );

      const titleEl = /** @type {HTMLElement | undefined} */ (this.refs.bookingTitle);
      if (titleEl) {
        titleEl.textContent = template?.dataset.bookingTitle || '';
      }

      const content = /** @type {HTMLElement} */ (this.refs.content);
      content.innerHTML = '';

      if (template) {
        const cloned = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
        sanitizeFragment(cloned);
        content.appendChild(cloned);
      } else {
        const error = document.createElement('p');
        error.className = 'booking-layer__error';
        error.textContent = this.dataset.error;
        content.appendChild(error);
      }

      this.open();
    }

    #clearContent() {
      const content = /** @type {HTMLElement | undefined} */ (this.refs.content);
      if (content) content.innerHTML = '';
    }
  }

  customElements.define('booking-layer', BookingLayer);
});
